/**
 * Customer Service - Server-side tracking via /api/track
 *
 * Previously wrote directly to Firebase. Now all writes go through the
 * server endpoint (protected by Firebase Admin SDK + security gates).
 * Read-only queries still use the client SDK where needed by admin UI.
 */

import { database } from '../firebase';
import { ref, get, query, orderByChild } from 'firebase/database';
import { apiCall } from '../api/config';

const hashContact = (str) => {
  const normalized = str.toLowerCase().trim();
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash) + normalized.charCodeAt(i);
    hash = hash & hash;
  }
  return 'c_' + Math.abs(hash).toString(36);
};

/**
 * Save a customer contact via server endpoint.
 * Returns the contactHash for use in later interactions.
 */
export const saveCustomerContact = async (contactData) => {
  if (!contactData?.value) {
    throw new Error('Contact value is required');
  }
  const contactHash = hashContact(contactData.value);
  try {
    await apiCall('/track', {
      method: 'POST',
      body: JSON.stringify({
        type: 'contact',
        data: {
          contactHash,
          contactType: contactData.type,
          value: contactData.value
        }
      })
    });
  } catch (err) {
    // Tracking failures must not break the user flow
    console.warn('Contact tracking failed (non-fatal):', err.message);
  }
  return contactHash;
};

/**
 * Track a customer interaction (try-on, scan, save) via server endpoint.
 */
export const trackInteraction = async (contactHash, interaction) => {
  if (!contactHash) return;
  try {
    await apiCall('/track', {
      method: 'POST',
      body: JSON.stringify({
        type: 'interaction',
        data: {
          contactHash,
          productId: interaction.productId,
          productName: interaction.productName,
          interactionType: interaction.type
        }
      })
    });
  } catch (err) {
    console.warn('Interaction tracking failed (non-fatal):', err.message);
  }
};

/**
 * Get all customers for admin view (read-only, uses Firebase rules).
 */
export const getAllCustomers = async () => {
  try {
    const customersRef = ref(database, 'customers');
    const customersQuery = query(customersRef, orderByChild('createdAt'));
    const snapshot = await get(customersQuery);

    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const customers = Object.keys(data).map(hash => {
      const customer = data[hash];
      const interactions = customer.interactions ? Object.values(customer.interactions) : [];
      return {
        hash,
        type: customer.type,
        value: customer.value,
        createdAt: customer.createdAt,
        lastSeen: customer.lastSeen,
        interactions,
        interactionCount: interactions.length
      };
    });
    customers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return customers;
  } catch (error) {
    console.error('Error loading customers:', error);
    throw error;
  }
};

export const getCustomerInteractions = async (contactHash) => {
  try {
    const interactionsRef = ref(database, `customers/${contactHash}/interactions`);
    const snapshot = await get(interactionsRef);
    if (!snapshot.exists()) return [];
    const data = snapshot.val();
    return Object.values(data).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  } catch (error) {
    console.error('Error loading customer interactions:', error);
    return [];
  }
};
